import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Row from "@amzn/meridian/row";
import SearchField from "@amzn/meridian/search-field";
import Text from "@amzn/meridian/text";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RecordLine from "src/components/shared/recordLine/recordLine";
import ScrollableSection from "src/components/shared/scrollableSection/scrollableSection";
import TimePeriodService, { TimePeriodType } from "src/services/timePeriodService";
import { getRandomInsight } from "src/store/modelLifeCycleGroundTruthSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import AccuracyTable from "./components/accuracyTable";
import InsightsCard from "./components/insightsCard";
import AutomatedJobsService, { BatchJobDefinition } from "../../../services/automatedJobsService";
import { Count, InsightsForm } from "./types";
import { setSelectedModelId } from "src/store/modelManagementSlice";
import { ENGINEER_LDAP_GROUP } from "src/constants";

const AutomatedJobs = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { jobId } = useParams();

  const { automatedJobs } = useAppSelector((state) => state.automatedBatchJobSlice);
  const { ldap } = useAppSelector((state) => state.user);

  const [filteredJobs, setFilteredJobs] = useState<BatchJobDefinition[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedJob, setSelectedJob] = useState<BatchJobDefinition>();
  const [insightsForm, setInsightsForm] = useState<InsightsForm>({
    selectedPeriodType: TimePeriodType.WEEKLY,
    selectedCountIndex: 0,
  });
  const PERIOD_TYPE_OPTIONS = [
    TimePeriodType.WEEKLY,
    TimePeriodType.MONTHLY,
    TimePeriodType.QUARTERLY,
    TimePeriodType.YEARLY,
  ];
  const [counts, setCounts] = useState<Count[]>([]);
  const [accuracy, setAccuracy] = useState<any>();
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  useEffect(() => {
    filterJobs();
  }, [searchValue, automatedJobs]);

  useEffect(() => {
    if (!jobId || !automatedJobs.length) return;
    const job = automatedJobs.find((job) => job.id === jobId);

    if (job) {
      setSelectedJob(job);
    } else {
      navigate("/automatedJobs");
    }
  }, [jobId, automatedJobs]);

  useEffect(() => {
    if (selectedJob && insightsForm.selectedPeriodType) {
      loadCounts();
    }
  }, [insightsForm.selectedPeriodType, selectedJob]);

  useEffect(() => {
    if (selectedJob) {
      dispatch(setSelectedModelId(selectedJob.modelId));
    }
  }, [selectedJob]);

  useEffect(() => {
    if (selectedJob && selectedJob.id && counts[insightsForm.selectedCountIndex]) {
      loadAccuracy(selectedJob.id);
    }
  }, [counts[insightsForm.selectedCountIndex], selectedJob]);

  const loadAccuracy = async (definitionId: string) => {
    try {
      const startDate = counts[insightsForm.selectedCountIndex].periodStart.toISOString();
      const endDate = counts[insightsForm.selectedCountIndex].periodEnd.toISOString();
      const accuracyData = await AutomatedJobsService.getAccuracy({
        start_date: startDate,
        end_date: endDate,
        definition_id: definitionId,
      });
      setAccuracy(accuracyData);
    } finally {
    }
  };

  const loadCounts = async () => {
    if (!selectedJob?.id) return;

    setIsLoadingCounts(true);
    try {
      const data = await AutomatedJobsService.getCounts({
        start_date: TimePeriodService.startDate,
        end_date: TimePeriodService.endDate,
        definition_id: selectedJob.id,
        period: insightsForm.selectedPeriodType,
      });
      setCounts(data);
      setInsightsForm({ ...insightsForm, selectedCountIndex: 0 });
    } finally {
      setIsLoadingCounts(false);
    }
  };

  const onReviewClick = async () => {
    if (!selectedJob?.id) return;

    const startDate = counts[insightsForm.selectedCountIndex].periodStart.toISOString();
    const endDate = counts[insightsForm.selectedCountIndex].periodEnd.toISOString();
    const { sourceId } = await dispatch(
      getRandomInsight({
        start_date: startDate,
        end_date: endDate,
        definition_id: selectedJob.id,
      }),
    ).unwrap();
    navigate(
      `/automatedJobs/${selectedJob.id}/${sourceId}?startDate=${startDate}&endDate=${endDate}`,
    );
  };

  const filterJobs = () =>
    setFilteredJobs(
      searchValue
        ? automatedJobs.filter((job) => job.name.toLowerCase().includes(searchValue.toLowerCase()))
        : automatedJobs,
    );

  return (
    <Row alignmentVertical="top" widths={["grid-3", "fit", "fill"]} className="automated-jobs">
      <ScrollableSection spacingInset="500">
        <Column spacing="400">
          <Row alignmentVertical="center" widths={["fill", "fit"]}>
            <Text type="h300">Automated Jobs</Text>
            {ldap.includes(ENGINEER_LDAP_GROUP) && (
              <Button type="secondary" onClick={() => navigate("/automatedJobs/manage")}>
                Manage Jobs
              </Button>
            )}
          </Row>
          <SearchField onChange={setSearchValue} value={searchValue} onSubmit={filterJobs} />
        </Column>
        <Column spacing={"300"}>
          {filteredJobs.map((job) => (
            <RecordLine
              key={job.id}
              name={job.name}
              description={`${job.status}`}
              isSelected={selectedJob?.id === job.id}
              onClick={() => {
                setSelectedJob(job);
              }}
              data-cy={"automated-job__list"}
            />
          ))}
        </Column>
      </ScrollableSection>
      <div className="split-line"></div>
      {!!selectedJob && (
        <ScrollableSection spacingInset="500">
          <Heading level={1} type="d100">
            {selectedJob?.name}
          </Heading>
          <InsightsCard
            counts={counts}
            isLoading={isLoadingCounts}
            periodTypeOptions={PERIOD_TYPE_OPTIONS}
            insightsForm={insightsForm}
            averageAccuracy={accuracy?.average_accuracy}
            onSelectPeriodType={(selectedPeriodType) => {
              if (selectedPeriodType !== insightsForm.selectedPeriodType) {
                setInsightsForm({ ...insightsForm, selectedPeriodType });
                setCounts([]);
              }
            }}
            onSelectPeriodIndex={(selectedCountIndex) => {
              if (selectedCountIndex !== insightsForm.selectedCountIndex) {
                setInsightsForm({ ...insightsForm, selectedCountIndex });
              }
            }}
            onReviewClick={onReviewClick}
          />
          {accuracy
            ? (() => {
                const { average_accuracy, ...fieldAccuracy } = accuracy;
                return <AccuracyTable accuracy={fieldAccuracy} />;
              })()
            : ""}
        </ScrollableSection>
      )}
    </Row>
  );
};

export default AutomatedJobs;
